import { HUMAN_CAMPAIGN_TYPES } from '../../interfaces/campaign'
import { CAPITAL_RAISE_ROUND_TYPES } from '../../interfaces/crm'
import { ReferralSide } from '../../interfaces/overview'
import { FilterFieldType, IFilter } from './types'

// ---------------------------------------------------------------------------
// CRM "network rail" list endpoints.
//
// Each CRM detail page (company / person / organization) renders a stack of
// relationship tables (People, Related Companies, Referrals, …). They are all
// served by one generic list endpoint (`GET /crm/network/:kind/:id`) that loads
// the owner's relationship rows and applies the shared filter config below for
// filter + sort + pagination — the same configs drive the client filter UI and
// sortable headers, exactly like the other CRM lists.
//
// `field` matches the row-interface property name (see common/interfaces/overview),
// so the same key identifies the column, the filter param, and the sort key.
// These configs hold NO JSX (the server imports this file) — the cell rendering
// lives client-side in client/src/components/Crm/networkRows.tsx.
// ---------------------------------------------------------------------------

export enum NetworkListKind {
  OrganizationPeople = 'organizationPeople',
  OrganizationCompanies = 'organizationCompanies',
  OrganizationReferrals = 'organizationReferrals',
  PersonAffiliations = 'personAffiliations',
  PersonReferrals = 'personReferrals',
  PersonLinks = 'personLinks',
  PersonInvestments = 'personInvestments',
  CompanyPeople = 'companyPeople',
  CompanyOrganizations = 'companyOrganizations',
  CompanyReferrals = 'companyReferrals',
  CompanyCapitalRaises = 'companyCapitalRaises',
  CompanyCampaigns = 'companyCampaigns',
  OrganizationCampaigns = 'organizationCampaigns',
  PersonCampaigns = 'personCampaigns',
}

export const NETWORK_LIST_KINDS = Object.values(NetworkListKind)

// The owner entity each kind is scoped to (route `:id` is an id of this kind).
export const NETWORK_LIST_OWNER: Record<NetworkListKind, 'organization' | 'person' | 'company'> = {
  [NetworkListKind.OrganizationPeople]: 'organization',
  [NetworkListKind.OrganizationCompanies]: 'organization',
  [NetworkListKind.OrganizationReferrals]: 'organization',
  [NetworkListKind.PersonAffiliations]: 'person',
  [NetworkListKind.PersonReferrals]: 'person',
  [NetworkListKind.PersonLinks]: 'person',
  [NetworkListKind.PersonInvestments]: 'person',
  [NetworkListKind.CompanyPeople]: 'company',
  [NetworkListKind.CompanyOrganizations]: 'company',
  [NetworkListKind.CompanyReferrals]: 'company',
  [NetworkListKind.CompanyCapitalRaises]: 'company',
  [NetworkListKind.CompanyCampaigns]: 'company',
  [NetworkListKind.OrganizationCampaigns]: 'organization',
  [NetworkListKind.PersonCampaigns]: 'person',
}

const ACTIVE_STATUS_OPTIONS = ['Active', 'Inactive'].map((value) => ({ value, label: value }))

// Exported so the Capital Raises edit modal's Round/Type select and this rail's
// filter render the same list — they are the pair a reader assumes are in sync.
export const ROUND_TYPE_OPTIONS = CAPITAL_RAISE_ROUND_TYPES.map((value) => ({
  value,
  label: value,
}))

/**
 * How each `ReferralSide` reads as the counterparty's role. The side says which
 * end of the record the *other* party sits on, so 'Source' is the party who made
 * the referral and 'Target' is the party who was referred.
 *
 * Deliberately not labelled In/Out: the only Dwight-relative direction is the
 * Salesforce RecordType in `IReferralRow.type`, which reads the same on both
 * parties' pages. Shared with the row cells (`networkCells.tsx`) so the filter
 * option and the label on the row can't drift apart.
 */
export const REFERRAL_SIDE_LABEL: Record<ReferralSide, string> = {
  Source: 'Referrer',
  Target: 'Referred party',
}

const REFERRAL_SIDE_OPTIONS = Object.entries(REFERRAL_SIDE_LABEL).map(([value, label]) => ({
  value,
  label,
}))

// People affiliated with a company/organization (each links to a CRM person).
const PEOPLE_FILTERS = [
  {
    field: 'name',
    type: FilterFieldType.Text,
    title: 'Person',
    placeholder: 'Person',
    isSortable: true,
  },
  // Stays a substring Text filter even though the underlying column is text[]:
  // these rails filter in memory (crm/networkLists/filterRows.ts) over the row's
  // scalar `role` label, which is the person's roles already joined. A List /
  // isMultiple filter would compare a picked role against that whole label by
  // equality, so "Board Member" would stop matching a person tagged
  // "Investor, Board Member" — the exact case multi-role exists to support.
  {
    field: 'role',
    type: FilterFieldType.Text,
    title: 'Type',
    placeholder: 'Type',
    isSortable: true,
  },
  {
    field: 'status',
    type: FilterFieldType.List,
    title: 'Status',
    options: ACTIVE_STATUS_OPTIONS,
    excludeColumn: true,
  },
  { field: 'email', type: FilterFieldType.Text, title: 'Email', excludeColumn: true },
] as const satisfies readonly IFilter[]

// Companies related to an organization (each links to a CRM company page).
const RELATED_COMPANIES_FILTERS = [
  {
    field: 'name',
    type: FilterFieldType.Text,
    title: 'Company',
    placeholder: 'Company',
    isSortable: true,
  },
  {
    field: 'type',
    type: FilterFieldType.Text,
    title: 'Type',
    placeholder: 'Type',
    isSortable: true,
  },
  {
    field: 'status',
    type: FilterFieldType.List,
    title: 'Status',
    options: ACTIVE_STATUS_OPTIONS,
    excludeColumn: true,
  },
  { field: 'priority', type: FilterFieldType.Text, title: 'Priority', excludeColumn: true },
] as const satisfies readonly IFilter[]

// Organizations related to a company (each links to an organization page).
const RELATED_ORGANIZATIONS_FILTERS = [
  {
    field: 'name',
    type: FilterFieldType.Text,
    title: 'Organization',
    placeholder: 'Organization',
    isSortable: true,
  },
  {
    field: 'type',
    type: FilterFieldType.Text,
    title: 'Type',
    placeholder: 'Type',
    isSortable: true,
  },
  {
    field: 'status',
    type: FilterFieldType.List,
    title: 'Status',
    options: ACTIVE_STATUS_OPTIONS,
    excludeColumn: true,
  },
  { field: 'priority', type: FilterFieldType.Text, title: 'Priority', excludeColumn: true },
] as const satisfies readonly IFilter[]

// Both sides of a party's referrals merged into one table, filterable by the
// counterparty's role (see REFERRAL_SIDE_LABEL).
const REFERRALS_FILTERS = [
  {
    field: 'name',
    type: FilterFieldType.Text,
    title: 'Referral',
    placeholder: 'Referral',
    isSortable: true,
  },
  {
    field: 'date',
    type: FilterFieldType.Date,
    title: 'Date',
    isSortable: true,
    align: 'right',
  },
  // Matches IReferralRowWithSide.side — the row property the loaders emit. It
  // was 'direction' with Inbound/Outbound while the rows carried that field.
  {
    field: 'side',
    type: FilterFieldType.List,
    title: 'Their role',
    options: REFERRAL_SIDE_OPTIONS,
    excludeColumn: true,
  },
  { field: 'type', type: FilterFieldType.Text, title: 'Type', excludeColumn: true },
] as const satisfies readonly IFilter[]

// A person's affiliations (company/org they belong to; role edited inline).
const PERSON_AFFILIATIONS_FILTERS = [
  {
    field: 'name',
    type: FilterFieldType.Text,
    title: 'Company / Org',
    placeholder: 'Company / Org',
    isSortable: true,
  },
  // No column of its own: this rail renders in the person page's narrow right
  // column, so the period folds into the row's secondary line instead (see
  // buildPersonAffiliationCells). `excludeColumn` drops the header; the filter
  // and the sort both survive it.
  //
  // `isSortable` has to stay even with no header to click: Base
  // .buildFiltersValidationRules builds the `orderBy` allowlist from it, and
  // this is the rail's default sort (CRM_NETWORK_DEFAULT_SORT), so dropping it
  // makes every request to this rail fail LIVR with orderBy NOT_ALLOWED_VALUE
  // before any user input. It authorizes the field for sorting; it does not by
  // itself render a control.
  {
    field: 'startDate',
    type: FilterFieldType.Date,
    title: 'Since',
    isSortable: true,
    excludeColumn: true,
  },
  // Substring Text over the joined role label — see the PEOPLE_FILTERS note.
  { field: 'role', type: FilterFieldType.Text, title: 'Role', excludeColumn: true },
] as const satisfies readonly IFilter[]

// Person ↔ person relationships (the other side projected).
const PERSON_LINKS_FILTERS = [
  {
    field: 'name',
    type: FilterFieldType.Text,
    title: 'Person',
    placeholder: 'Person',
    isSortable: true,
  },
  {
    field: 'type',
    type: FilterFieldType.Text,
    title: 'Relationship',
    placeholder: 'Relationship',
    isSortable: true,
  },
] as const satisfies readonly IFilter[]

// A person's investments (into a company/organization).
const PERSON_INVESTMENTS_FILTERS = [
  {
    field: 'name',
    type: FilterFieldType.Text,
    title: 'Company / Org',
    placeholder: 'Company / Org',
    isSortable: true,
  },
  {
    field: 'amount',
    type: FilterFieldType.Amount,
    title: 'Amount',
    isSortable: true,
    align: 'right',
  },
  { field: 'round', type: FilterFieldType.Text, title: 'Round', excludeColumn: true },
] as const satisfies readonly IFilter[]

// A company's capital raises — one row per financing round. The investor and
// source columns are to-many / composite, so they filter and sort against the
// rolled-up scalars the loader emits (leadInvestorName,
// participatingInvestorsLabel, sourceLabel) rather than the nested arrays.
const CAPITAL_RAISES_FILTERS = [
  {
    field: 'date',
    type: FilterFieldType.Date,
    title: 'Date',
    // `isSortable` is load-bearing: Base.buildFiltersValidationRules builds the
    // `orderBy` allowlist from it, and this is the rail's default sort
    // (CRM_NETWORK_DEFAULT_SORT). Dropping it makes every request to this rail
    // fail LIVR with orderBy NOT_ALLOWED_VALUE before any user input.
    isSortable: true,
  },
  {
    field: 'roundType',
    type: FilterFieldType.List,
    title: 'Round / Type',
    options: ROUND_TYPE_OPTIONS,
    isSortable: true,
  },
  {
    field: 'amount',
    type: FilterFieldType.Amount,
    title: 'Amount',
    isSortable: true,
    align: 'right',
  },
  {
    field: 'leadInvestorName',
    type: FilterFieldType.Text,
    title: 'Lead Investor',
    placeholder: 'Lead Investor',
    isSortable: true,
  },
  {
    field: 'participatingInvestorsLabel',
    type: FilterFieldType.Text,
    title: 'Participating Investors',
    placeholder: 'Investor',
  },
  { field: 'sourceLabel', type: FilterFieldType.Text, title: 'Source' },
  // Filterable but not sortable: ordering rows by free-text notes has no meaning,
  // and `isSortable` is what admits a field to the orderBy allowlist.
  { field: 'notes', type: FilterFieldType.Text, title: 'Notes', placeholder: 'Notes' },
] as const satisfies readonly IFilter[]

// Campaigns a person/company/organization is a direct member of. Field names avoid the
// rails' shared `type`/`status` keys: CRM_NETWORK_UNION_FILTERS dedupes by field,
// so reusing `status` would validate member statuses against Active/Inactive.
const CAMPAIGNS_FILTERS = [
  {
    field: 'name',
    type: FilterFieldType.Text,
    title: 'Campaign',
    placeholder: 'Campaign',
    isSortable: true,
  },
  {
    field: 'campaignType',
    type: FilterFieldType.List,
    title: 'Type',
    options: HUMAN_CAMPAIGN_TYPES.map((value) => ({ value, label: value })),
    isSortable: true,
  },
  {
    field: 'startDate',
    type: FilterFieldType.Date,
    title: 'Date',
    isSortable: true,
    align: 'right',
  },
  { field: 'memberStatus', type: FilterFieldType.Text, title: 'Status', excludeColumn: true },
] as const satisfies readonly IFilter[]

export const CRM_NETWORK_FILTERS: Record<NetworkListKind, readonly IFilter[]> = {
  [NetworkListKind.OrganizationPeople]: PEOPLE_FILTERS,
  [NetworkListKind.OrganizationCompanies]: RELATED_COMPANIES_FILTERS,
  [NetworkListKind.OrganizationReferrals]: REFERRALS_FILTERS,
  [NetworkListKind.PersonAffiliations]: PERSON_AFFILIATIONS_FILTERS,
  [NetworkListKind.PersonReferrals]: REFERRALS_FILTERS,
  [NetworkListKind.PersonLinks]: PERSON_LINKS_FILTERS,
  [NetworkListKind.PersonInvestments]: PERSON_INVESTMENTS_FILTERS,
  [NetworkListKind.CompanyPeople]: PEOPLE_FILTERS,
  [NetworkListKind.CompanyOrganizations]: RELATED_ORGANIZATIONS_FILTERS,
  [NetworkListKind.CompanyReferrals]: REFERRALS_FILTERS,
  [NetworkListKind.CompanyCapitalRaises]: CAPITAL_RAISES_FILTERS,
  [NetworkListKind.CompanyCampaigns]: CAMPAIGNS_FILTERS,
  [NetworkListKind.OrganizationCampaigns]: CAMPAIGNS_FILTERS,
  [NetworkListKind.PersonCampaigns]: CAMPAIGNS_FILTERS,
}

// Default sort per kind (field + direction) — newest/most-relevant first.
export const CRM_NETWORK_DEFAULT_SORT: Record<
  NetworkListKind,
  { field: string; direction: 'ASC' | 'DESC' }
> = {
  [NetworkListKind.OrganizationPeople]: { field: 'name', direction: 'ASC' },
  [NetworkListKind.OrganizationCompanies]: { field: 'name', direction: 'ASC' },
  [NetworkListKind.OrganizationReferrals]: { field: 'date', direction: 'DESC' },
  [NetworkListKind.PersonAffiliations]: { field: 'startDate', direction: 'DESC' },
  [NetworkListKind.PersonReferrals]: { field: 'date', direction: 'DESC' },
  [NetworkListKind.PersonLinks]: { field: 'name', direction: 'ASC' },
  [NetworkListKind.PersonInvestments]: { field: 'amount', direction: 'DESC' },
  [NetworkListKind.CompanyPeople]: { field: 'name', direction: 'ASC' },
  [NetworkListKind.CompanyOrganizations]: { field: 'name', direction: 'ASC' },
  [NetworkListKind.CompanyReferrals]: { field: 'date', direction: 'DESC' },
  [NetworkListKind.CompanyCapitalRaises]: { field: 'date', direction: 'DESC' },
  [NetworkListKind.CompanyCampaigns]: { field: 'startDate', direction: 'DESC' },
  [NetworkListKind.OrganizationCampaigns]: { field: 'startDate', direction: 'DESC' },
  [NetworkListKind.PersonCampaigns]: { field: 'startDate', direction: 'DESC' },
}

// Every distinct filter field across all rails, deduped by `field` — used to build
// one static LIVR validation rule set for the shared NetworkList service (LIVR's
// nested_object drops keys a given kind doesn't send, so a superset is safe).
export const CRM_NETWORK_UNION_FILTERS: IFilter[] = Object.values(CRM_NETWORK_FILTERS)
  .flat()
  .reduce<IFilter[]>((acc, filter) => {
    if (!acc.some((existing) => existing.field === filter.field)) acc.push(filter)
    return acc
  }, [])
